import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Award,
  Plus,
  Upload,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trust state badge styling
const getTrustStateBadge = (trustState) => {
  if (!trustState) return null;
  
  const { state, label, verified, color } = trustState;
  
  if (verified) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  }
  
  return (
    <Badge variant="secondary" className="bg-gray-100 text-gray-600">
      <Clock className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

export default function Certificates() {
  const { token } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    issuer: '',
    issue_date: '',
    expiry_date: '',
    credential_id: '',
    verification_url: '',
    visibility_included: true,
  });
  const [file, setFile] = useState(null);

  const fetchCertificates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/certificates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCertificates(data);
      }
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (file) {
        // Upload with file
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        formDataObj.append('name', formData.name);
        formDataObj.append('issuer', formData.issuer);
        if (formData.issue_date) formDataObj.append('issue_date', formData.issue_date);
        if (formData.expiry_date) formDataObj.append('expiry_date', formData.expiry_date);
        if (formData.credential_id) formDataObj.append('credential_id', formData.credential_id);
        if (formData.verification_url) formDataObj.append('verification_url', formData.verification_url);
        formDataObj.append('visibility_included', formData.visibility_included);
        
        response = await fetch(`${API_URL}/api/certificates/with-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formDataObj,
        });
      } else {
        // Metadata only
        response = await fetch(`${API_URL}/api/certificates`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      }
      
      if (response.ok) {
        toast.success('Certificate added successfully');
        setShowAddDialog(false);
        resetForm();
        fetchCertificates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add certificate');
      }
    } catch (error) {
      console.error('Failed to add certificate:', error);
      toast.error('Failed to add certificate');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      issuer: '',
      issue_date: '',
      expiry_date: '',
      credential_id: '',
      verification_url: '',
      visibility_included: true,
    });
    setFile(null);
  };

  const handleVerify = async (certId) => {
    setVerifying(certId);
    try {
      const response = await fetch(`${API_URL}/api/certificates/${certId}/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Certificate verified via ${result.method}`);
        fetchCertificates();
      } else {
        toast.error(result.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleToggleVisibility = async (certId, currentVisibility) => {
    try {
      const endpoint = currentVisibility ? 'make-private' : 'make-public';
      const response = await fetch(`${API_URL}/api/certificates/${certId}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success(`Certificate ${currentVisibility ? 'hidden from' : 'visible on'} public profile`);
        fetchCertificates();
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/certificates/${certId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Certificate deleted');
        fetchCertificates();
      }
    } catch (error) {
      console.error('Failed to delete certificate:', error);
      toast.error('Failed to delete certificate');
    }
  };

  const handleRequestReview = async (certId) => {
    const reason = window.prompt('Why should we manually review this certificate?');
    if (!reason) return;
    
    try {
      const response = await fetch(`${API_URL}/api/certificates/${certId}/request-review`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });
      
      if (response.ok) {
        toast.success('Review request submitted');
        fetchCertificates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to request review');
      }
    } catch (error) {
      console.error('Failed to request review:', error);
      toast.error('Failed to request review');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certificates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6 text-primary" />
            Certifications & Learning
          </h1>
          <p className="text-muted-foreground mt-1">
            Add and verify your professional certifications, degrees, and badges
          </p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-certificate-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Certificate
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Certificate</DialogTitle>
              <DialogDescription>
                Add a new certification to your professional passport
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Certificate Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., AWS Solutions Architect"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="cert-name-input"
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="issuer">Issuing Organization *</Label>
                  <Input
                    id="issuer"
                    placeholder="e.g., Amazon Web Services"
                    value={formData.issuer}
                    onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                    required
                    data-testid="cert-issuer-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="issue_date">Issue Date</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="expiry_date">Expiry Date</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="credential_id">Credential ID</Label>
                  <Input
                    id="credential_id"
                    placeholder="e.g., ABC123XYZ"
                    value={formData.credential_id}
                    onChange={(e) => setFormData({ ...formData, credential_id: e.target.value })}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="verification_url">Verification URL</Label>
                  <Input
                    id="verification_url"
                    type="url"
                    placeholder="https://credly.com/badges/..."
                    value={formData.verification_url}
                    onChange={(e) => setFormData({ ...formData, verification_url: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste the public verification link for automatic verification
                  </p>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="file">Certificate File (Optional)</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                  <p className="text-xs text-muted-foreground">
                    PDF or image, max 10MB. Files are stored securely and never shared publicly.
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="visibility"
                    checked={formData.visibility_included}
                    onCheckedChange={(checked) => setFormData({ ...formData, visibility_included: checked })}
                  />
                  <Label htmlFor="visibility">Show on public profile</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-certificate-btn">
                  Add Certificate
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Trust States Explanation */}
      <Card className="bg-blue-50/50 border-blue-100">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900">How certificate verification works</p>
              <p className="text-blue-700 mt-1">
                Certificates display one of three trust states:{' '}
                <span className="font-medium">Self-Reported</span> (uploaded, not yet verified),{' '}
                <span className="font-medium">Issuer Verified</span> (confirmed with the issuing organization), or{' '}
                <span className="font-medium">VETT Reviewed</span> (manually confirmed by our team).
                Only verified certificates contribute to your trust score.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificates List */}
      {certificates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Award className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No certificates yet</h3>
            <p className="text-muted-foreground text-center mt-1 max-w-md">
              Add your professional certifications, degrees, and badges to build a complete
              picture of your qualifications.
            </p>
            <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Certificate
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {certificates.map((cert) => (
            <Card key={cert.id} data-testid={`certificate-card-${cert.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{cert.name}</h3>
                      {getTrustStateBadge(cert.trust_state)}
                    </div>
                    
                    <p className="text-muted-foreground">{cert.issuer}</p>
                    
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                      {cert.issue_date && (
                        <span>Issued: {new Date(cert.issue_date).toLocaleDateString()}</span>
                      )}
                      {cert.expiry_date && (
                        <span>Expires: {new Date(cert.expiry_date).toLocaleDateString()}</span>
                      )}
                      {cert.has_file && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          File attached
                        </span>
                      )}
                    </div>
                    
                    {/* Manual Review Status */}
                    {cert.manual_review && (
                      <div className="mt-3">
                        <Badge variant={cert.manual_review.status === 'pending' ? 'outline' : 'secondary'}>
                          {cert.manual_review.status === 'pending' ? 'Review Pending' : 
                           cert.manual_review.status === 'approved' ? 'Review Approved' : 'Review Rejected'}
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Visibility Toggle */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleVisibility(cert.id, cert.visibility_included)}
                      title={cert.visibility_included ? 'Hide from public' : 'Show on public'}
                    >
                      {cert.visibility_included ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                    
                    {/* Verify Button */}
                    {cert.trust_state?.state === 'self_reported' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerify(cert.id)}
                        disabled={verifying === cert.id}
                        data-testid={`verify-cert-${cert.id}`}
                      >
                        {verifying === cert.id ? (
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-1" />
                        )}
                        Verify
                      </Button>
                    )}
                    
                    {/* Request Review Button */}
                    {cert.trust_state?.state === 'self_reported' && !cert.manual_review && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRequestReview(cert.id)}
                        title="Request manual review"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Delete Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(cert.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
